// apps/web/src/lib/oura.ts
//
// The product's display rules, in one place: bands render as words, positions
// render as color-graded chips, and no derived metric survives a base under 30.
// These encode §4 of the brief. Screens read these, they don't reinvent them.

import type { Band } from '../contract';

/** Pillars render as words, never scores out of anything. */
export const BAND_WORD: Record<Band, string> = {
  not_started: 'Not started',
  weak: 'Weak',
  getting_there: 'Getting there',
  good: 'Good',
  strong: 'Strong',
};

/** Band → the canonical 5-stop semantic scale (light bg + dark text). */
export const BAND_CHIP: Record<Band, string> = {
  not_started: 'bg-gray-100 text-gray-500 border-gray-200',
  weak: 'bg-rose-50 text-rose-700 border-rose-200',
  getting_there: 'bg-amber-50 text-amber-700 border-amber-200',
  good: 'bg-blue-50 text-blue-700 border-blue-200',
  strong: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

/** Bar fill color for a band, used on the pillar bars. */
export const BAND_BAR: Record<Band, string> = {
  not_started: 'bg-gray-300',
  weak: 'bg-rose-400',
  getting_there: 'bg-amber-400',
  good: 'bg-blue-500',
  strong: 'bg-emerald-500',
};

/**
 * A Google position is deterministic, so it renders as a solid chip:
 * green top three, amber four to ten, red beyond, grey when absent.
 */
export function positionChip(pos: number | null): { label: string; className: string } {
  if (pos === null) return { label: '—', className: 'bg-gray-100 text-gray-400 border-gray-200' };
  if (pos <= 3) return { label: `#${pos}`, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (pos <= 10) return { label: `#${pos}`, className: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { label: `#${pos}`, className: 'bg-rose-50 text-rose-700 border-rose-200' };
}

/** Suppress any derived metric whose base is under 30. */
export const MIN_BASE = 30;
export function enoughBase(base: number): boolean {
  return base >= MIN_BASE;
}

/** The five rungs of the ladder, low to high. */
export const LEVEL_NAMES = [
  'Getting started',
  'Finding your feet',
  'Getting there',
  'Well placed',
  'Leading',
];

export const ENGINE_LABEL: Record<string, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
  perplexity: 'Perplexity',
  // The one definition (D5): every screen, the drawer and the CSV read it here.
  ai_overview: "Google's AI",
};

/**
 * The id an engine carries on a probe or in the answer store ("openai") mapped
 * onto the FE's engine key ("chatgpt"). Kept next to ENGINE_LABEL because both
 * the question grid and the stored-answers panel must name an engine the same
 * way — a reader seeing "openai" in one place and "ChatGPT" in another would
 * reasonably think they are two different engines.
 */
export const PROBE_ENGINE_KEY: Record<string, string> = {
  openai: 'chatgpt',
  chatgpt: 'chatgpt',
  // The ChatGPT app run (logged out, in a browser) is stored under its own
  // engine id. It is still ChatGPT to a reader, and where it exists for a
  // question it is the verdict over the `openai` API row.
  chatgpt_app: 'chatgpt',
  gemini: 'gemini',
  google: 'gemini',
};

/** Human name for any engine id, falling back to the id itself. */
export function engineDisplayName(engine: string): string {
  const raw = (engine ?? '').toLowerCase();
  return ENGINE_LABEL[PROBE_ENGINE_KEY[raw] ?? raw] ?? engine;
}

/** '2026-07' → 'July 2026'. Deterministic, no Date needed for the month name. */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function fmtDay(d: Date, withYear = false): string {
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}${withYear ? ` ${d.getFullYear()}` : ''}`;
}

/** '2026-09-01' → 'Sep', or 'Sep 2026' with the year: a month's own label. Read off the string, so no time zone can move it. */
export function shortMonth(dateStr: string, withYear = false): string {
  const [y, m] = dateStr.split('-');
  const name = MONTHS_SHORT[Number(m) - 1];
  if (!name) return '–';
  return withYear ? `${name} ${y}` : name;
}

/**
 * Parse either a date-only string ('2026-07-21') or a full ISO timestamp
 * ('2026-07-28T10:14:32.511Z'). Fixture data is date-only; every real BE field
 * (`capturedAt`, `captured_at`, `created_at`) is a full timestamp — blindly
 * appending "T00:00:00" to the latter produced Invalid Date, which rendered as
 * "NaN undefined NaN" on screen.
 */
function parseDateish(value: string): Date {
  return new Date(value.length <= 10 ? `${value}T00:00:00` : value);
}

/** The Monday to Sunday week containing a captured date, e.g. "6 to 12 Jul 2026". */
export function weekRange(dateStr: string): string {
  const d = parseDateish(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const sameMonth = monday.getMonth() === sunday.getMonth();
  return sameMonth
    ? `${monday.getDate()} to ${sunday.getDate()} ${MONTHS_SHORT[sunday.getMonth()]} ${sunday.getFullYear()}`
    : `${fmtDay(monday)} to ${fmtDay(sunday, true)}`;
}

/** A short date `n` days after `dateStr`, e.g. "13 Jul". */
export function dateAfter(dateStr: string, days: number): string {
  const d = parseDateish(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  d.setDate(d.getDate() + days);
  return fmtDay(d, true);
}

/** '2026-07-21' → '21 Jul 2026'. A discovered/captured date with the year. */
/**
 * Parts of a one-line identity ("domain · category · city · Last scan …")
 * joined with " · ", skipping the ones that are empty.
 *
 * Joined with a fixed separator between every slot, a business with no city
 * read "Dental Clinic · · Last scan 1 Sept 2026" — two dots for a value that
 * was never there.
 */
export function joinDotted(...parts: Array<string | null | undefined>): string {
    return parts.map((p) => (p ?? "").trim()).filter(Boolean).join(" · ");
}

export function shortDate(dateStr: string): string {
  const d = parseDateish(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return fmtDay(d, true);
}

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * '2026-09-22' → 'Tue 22 Sep 2026': a merchant date ("Reply by", "Asked", the
 * next report), weekday first. Always with the year (23 Oct 2026): a plan
 * runs across a new year, and "Fri 8 Jan" left which January unsaid.
 * Formatting only: no arithmetic on today. Null for a missing or unreadable
 * value, so the caller leaves the clause out rather than printing a glyph.
 */
export function dayDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = parseDateish(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${DAYS_SHORT[d.getDay()]} ${fmtDay(d, true)}`;
}

/** `shortDate` ('16 Sep 2026'), but null for a missing or unreadable value instead of a placeholder glyph. */
export function shortDateOrNull(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = parseDateish(value);
  return Number.isNaN(d.getTime()) ? null : fmtDay(d, true);
}

/** '2026-07-06T17:30' → '6 Jul 2026 at 5.30PM'. Date-only input works too. */
export function discoveredDateTime(iso: string): string {
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const time = m === 0 ? `${h}${ampm}` : `${h}.${m.toString().padStart(2, '0')}${ampm}`;
  return `${fmtDay(d, true)} at ${time}`;
}

/** The prefixed form: 'Discovered on: 6 Jul 2026 at 5.30PM'. */
export function discoveredLabel(iso: string): string {
  return `Discovered on: ${discoveredDateTime(iso)}`;
}

/**
 * A safe href for a crawled page or a domain, or `null` when there isn't one.
 *
 * The crawler stores ABSOLUTE urls (`_normalize_url()` resolves every
 * discovered link against the base), while fixtures and the detect payload
 * carry bare domains. Prefixing `https://` unconditionally turned an absolute
 * url into `https://https://example.com/page`, which the browser reads as the
 * host `https` and fails with DNS_PROBE_FINISHED_NXDOMAIN — so every affected
 * page link in the Site Audit was dead.
 *
 * These values come off a crawler, so the scheme is not ours to trust: only
 * http/https/mailto/tel produce an href. `javascript:` is the one that matters
 * — a crawled `javascript://x%0Aalert(1)` rendered into an `href` runs in our
 * origin on click. Everything else returns `null` and the caller shows plain
 * text instead of a link.
 *
 * Scheme-matching is on the colon, not `://`: `mailto:` and `tel:` have no
 * slashes, and the previous `://` test turned them into `https://mailto:…`.
 */
/**
 * An LLM's answer text, flattened to prose (#3403).
 *
 * Engine summaries arrive as the model's own Markdown, and rendering them
 * verbatim put literal `###` and `**` in a client-facing card. This strips the
 * transport format rather than rendering it — a one-line summary has no use
 * for headings or bold, and stripping cannot inject markup.
 *
 * Deliberately NOT handled: single-underscore emphasis. `_x_` is far more
 * likely to be a real identifier in this data (`dup_titles`, snake_case URLs)
 * than italics, and eating those underscores would corrupt the text.
 */
export function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' ') // fenced code blocks
    .replace(/^#{1,6}\s+/gm, '') // heading markers at line start
    .replace(/\s#{2,}\s/g, ' ') // inline "### " run glued mid-sentence
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/__([^_]+)__/g, '$1') // bold, underscore form
    .replace(/\*([^*\n]+)\*/g, '$1') // italics
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links -> their text
    .replace(/^\s*[-*+]\s+/gm, '· ') // list markers -> middle dot
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

const SAFE_HREF_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

export function pageHref(urlOrDomain: string): string | null {
  const value = (urlOrDomain || '').trim();
  if (!value) return null;
  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(value);
    const url = new URL(hasScheme ? value : `https://${value}`);
    return SAFE_HREF_PROTOCOLS.has(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
}

/**
 * The part of a URL that actually differs between rows: the path.
 *
 * Every affected page of an issue shares a host, so a list truncating at
 * `https://beyondstud…` renders nineteen identical-looking rows. The path is
 * the only distinguishing part, so it leads; the full URL stays on the link's
 * href and title attribute.
 *
 * Returns "/" for a bare origin, and the input unchanged if it will not parse
 * or carries an unsafe scheme (fixtures carry bare paths already). This is
 * display text, never an href, so an unsafe value is shown rather than dropped.
 */
/**
 * A page URL reduced to the thing two lists can actually be joined on.
 *
 * The same page reaches this app spelled several ways — with and without the
 * scheme, with and without `www.`, with and without a trailing slash — because
 * the crawler, the role overrides table and the audit checks each record
 * whatever they were given. Comparing the raw strings silently misses matches,
 * and a missed match here means a page the reviewer set aside comes back.
 */
export function pageUrlKey(url: string | undefined): string {
    return (url || "")
        .trim()
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "")
        .replace(/\/+$/, "")
        .toLowerCase();
}

export function pagePath(url: string): string {
  const value = (url || '').trim();
  if (!value) return '';
  const href = pageHref(value);
  if (!href) return value;
  try {
    const u = new URL(href);
    const path = `${u.pathname}${u.search}`;
    return path === '' ? '/' : path;
  } catch {
    return value;
  }
}

/** 'A separate page…' → initials 'RD' for an avatar tile. */
export function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * What a citation count counts, in the word the screen prints (#3890).
 *
 * Since ourteam-backend#5029/#5030 the own-site share and the source list are
 * counted PER QUESTION, out of the question set: a question counts once when
 * any engine that shows sources cited that site. Before that they counted
 * answers, and a scan could read "25 of 100" because two engines each answered
 * fifty times.
 *
 * READ THE BASIS, NEVER ASSUME. The numbers were already right when this
 * shipped; only the unit word was wrong, and hard-coding "questions" would
 * simply move the lie to the older scans — and to the one live business whose
 * share is still counted in discovery answers.
 */
export function countedUnit(basis: string | null | undefined): "question" | "answer" {
    return typeof basis === "string" && basis.indexOf("question") === 0 ? "question" : "answer";
}

/** `1 question` / `25 questions`, in whichever unit the basis names. */
export function countedUnitLabel(n: number, basis: string | null | undefined): string {
    const unit = countedUnit(basis);
    return `${unit}${n === 1 ? "" : "s"}`;
}

/** `"Gemini"`, `"Gemini and ChatGPT"`, `"A, B and C"` — one joining rule. */
export function joinNames(names: string[]): string {
    if (names.length === 0) return "";
    if (names.length === 1) return names[0];
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
